<template>
  <li class="nav-item position-relative">
    <button ref="navbarToggle"
            type="button"
            class="vega-hbtn"
            :class="{ 'is-open': navbarDropdownOpen }"
            :aria-label="$t('Notifications')"
            :title="$t('Notifications')"
            aria-haspopup="true"
            :aria-expanded="navbarDropdownOpen ? 'true' : 'false'"
            @click="toggleNavbarDropdown">
      <i class="fa-regular fa-bell" aria-hidden="true"></i>
      <span v-if="totalCount > 0" class="vega-hbtn__badge">{{ totalCount > 9 ? '9+' : totalCount }}</span>
    </button>
    <div v-show="navbarDropdownOpen" ref="navbarMenu" class="vega-hmenu vega-hmenu--wide">
      <div class="vega-hmenu__head">
        <h2 class="vega-hmenu__title">{{ $t('Notifications') }}</h2>
        <span v-if="totalCount > 0" class="vega-hmenu__count">{{ totalCount }}</span>
      </div>
      <div v-if="totalCount > 0" class="vega-hmenu__scroll">
        <!-- Sistem bildirimleri Vega zilinde yok; destek cevabı geldiğini buradan görürler. -->
        <div v-for="ticket in unreadTickets" :key="'ticket-' + ticket.id" class="vega-notif is-link" @click="openTicket(ticket.id)">
          <div class="vega-notif__body">
            <div class="vega-notif__row">
              <span class="vega-notif__name">{{ $t('HelpdeskComponent.NewAnswerNotification') }}</span>
              <span class="vega-notif__date">{{ $dateFormat(ticket.modified) }}</span>
            </div>
            <p class="vega-notif__text">{{ ticket.header }}</p>
          </div>
        </div>
        <div v-for="item in notifications" :key="item.id" class="vega-notif" :class="{ 'is-link': item.customerID != null }">
          <!-- Danışan notu: kutu notu tamamlar, satırın geri kalanı danışanın notlarını açar. -->
          <template v-if="item.customerID != null">
            <div class="vega-notif__check">
              <form-checkbox v-model="item.selected" :aria-label="$t('MarkAsDone')" :title="$t('MarkAsDone')" @change="completeNote(item.id)" />
            </div>
            <div class="vega-notif__body" @click="openSummaryNotePage(item.customerID)">
              <div class="vega-notif__row">
                <span class="vega-notif__name">{{ item.customerName }}</span>
                <span class="vega-notif__date">{{ $dateFormat(item.date) }}</span>
              </div>
              <p class="vega-notif__text">{{ item.notificationText }}</p>
            </div>
          </template>
          <div v-else class="vega-notif__body">
            <div class="vega-notif__row">
              <span class="vega-notif__name">{{ item.staffName }}</span>
              <span class="vega-notif__date">{{ $dateFormat(item.date) }}</span>
            </div>
            <p class="vega-notif__text">{{ item.notificationText }}</p>
          </div>
        </div>
      </div>
      <div v-else class="vega-hmenu__empty">
        <i class="fa-regular fa-bell-slash" aria-hidden="true"></i>
        <span>{{ $t('NoNotification') }}</span>
      </div>
    </div>
    <div ref="notification_refresher" id="notification_refresher" class="d-none" @click="getNotifications()" />
  </li>
</template>

<script>
  import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
  import { refreshSmartDuoData, refreshNotification } from '../../helpers/dataRefresher';
  import commonService from '../../services/common';
  import customerNoteService from '../../services/customerNote';
  import ticketService from '../../services/ticket';
  import { bus } from '../../plugins/event-bus';
  import navbarDropdown from './navbars/navbarDropdown';

  const UNREAD_TICKETS_RECHECK_MS = 5 * 60 * 1000;

  export default {
    name: 'Notifications',
    mixins: [navbarDropdown],
    components: {

    },
    data() {
      return {
        notifications: [],
        unreadTickets: [],
        unreadTicketsCheckedAt: 0
      }
    },
    computed: {
      totalCount() {
        return this.notifications.length + this.unreadTickets.length;
      }
    },
    watch: {
      // Cevap anında hub ile gelir. Hub yalnız talebi açanı uyarır ve kopabilir; diğer personel ve kopan
      // bağlantı için gezinmede en fazla beş dakikada bir yeniden bakılır.
      $route() {
        if (Date.now() - this.unreadTicketsCheckedAt > UNREAD_TICKETS_RECHECK_MS) {
          this.getUnreadTickets();
        }
      }
    },
    methods: {
      getUnreadTickets() {
        this.unreadTicketsCheckedAt = Date.now();
        ticketService.getUnreadAnswers()
          .then(tickets => {
            this.unreadTickets = tickets || [];
          })
          .catch(() => {
            this.unreadTickets = [];
          });
      },
      openTicket(ticketId) {
        if (this.$route.name == 'helpdesk.list' && Number(this.$route.query.ticketId) == ticketId)
          return;

        this.$router.push({ name: 'helpdesk.list', query: { ticketId } });
      },
      // 007 cevap yazınca sunucu talebi açan personele `onNotificationsChanged` gönderir; zil sayfa
      // değişimini beklemeden tazelenir. Hub başka olaylar da yollar; Vega dinlemediği için uyarı basılmaz.
      connectHub() {
        this.hub = new HubConnectionBuilder()
          .withUrl('/stellaHub', {
            accessTokenFactory: () => (this.$store.getters['Auth/token'] || '').replace(/^Bearer /, '')
          })
          .configureLogging(LogLevel.Error)
          .withAutomaticReconnect()
          .build();
        this.hub.on('onNotificationsChanged', () => {
          this.getNotifications();
          bus.$emit('ticketAnswered');
        });
        this.hub.start().catch(() => {});
      },
      getNotifications() {
        this.getUnreadTickets();
        commonService.getNotifications().then((response) => {
          if (response) {
            this.notifications = (response.personelNotifications || []).map(item => ({
              ...item,
              selected: false
            }));
          }
        })
      },
      completeNote(noteId) {
        customerNoteService.completeNote(noteId).then((response) => {
          if (response) {
            this.$toastr.success(this.$t('Success'));
            refreshSmartDuoData();
            refreshNotification();

            this.notifications = this.notifications.map(item => {
              if (item.id === noteId) {
                item.selected = false;
              }
              return item;
            });
          }
          else {
            this.$toastr.error(this.$t('Error'));
          }

          this.getNotifications();
        })

        this.notifications = this.notifications.map(item => {
          if (item.id === noteId) {
            item.selected = false;
          }
          return item;
        });
      },
      openSummaryNotePage(customerId) {
        if (this.$route.name == 'customerSummary.notes' && this.$route.params.customerId == customerId)
          return;
        else
          this.$router.push({ name: 'customerSummary.notes', params: { customerId: customerId } });
      },
    },
    mounted() {
      this.getNotifications();
      this.connectHub();
    },
    beforeUnmount() {
      if (this.hub) {
        this.hub.stop();
      }
    }
  }
</script>

<style src="../../assets/css/header-menu.css"></style>
